import type { TransactionDetails } from '@safe-global/store/gateway/AUTO_GENERATED/transactions'
import type { ConnectedWallet } from '@/hooks/wallets/useOnboard'
import { isMultisigExecutionInfo } from '@/utils/transaction-guards'
import { isEthSignWallet, isSmartContractWallet } from '@/utils/wallets'
import { buildSignatureBytes, type MultiSendCallOnlyContractImplementationType } from '@safe-global/protocol-kit'
import { cgwApi as relayApi } from '@safe-global/store/gateway/AUTO_GENERATED/relay'
import { getStoreInstance } from '@/store'
import { type Chain } from '@safe-global/store/gateway/AUTO_GENERATED/chains'
import { type SafeState } from '@safe-global/store/gateway/AUTO_GENERATED/safes'

import type {
  SafeSignature,
  SafeTransaction,
  Transaction,
  TransactionOptions,
  TransactionResult,
} from '@safe-global/types-kit'
import { didRevert } from '@/utils/ethers-utils'
import type { Eip1193Provider, Overrides, TransactionResponse } from 'ethers'
import type { RequestId } from '@safe-global/safe-apps-sdk'
import proposeTx from '../proposeTransaction'
import confirmTx from '../confirmTransaction'
import { txDispatch, TxEvent } from '../txEvents'
import { waitForRelayedTx } from '@/services/tx/txMonitor'
import { getReadOnlyCurrentGnosisSafeContract } from '@/services/contracts/safeContracts'
import {
  getAndValidateSafeSDK,
  getSafeSDKWithSigner,
  tryOffChainTxSigning,
  getUncheckedSigner,
  prepareTxExecution,
  prepareApproveTxHash,
} from './sdk'
import { createWeb3, getUserNonce } from '@/hooks/wallets/web3'
import { asError } from '@safe-global/utils/services/exceptions/utils'
import chains from '@safe-global/utils/config/chains'
import { createExistingTx } from './create'
import { getRelaySimulationError } from '@safe-global/utils/services/relayErrors'
import { getQuotaExceededError, QuotaExceededError } from '@safe-global/utils/services/quotaErrors'
import {
  GasPaymentOptionUnavailableError,
  getGasPaymentOptionUnavailableError,
  getRelayerUnavailableError,
  getRelayLimitReachedError,
} from '@safe-global/utils/services/gasPaymentErrors'
import { refreshSpaceEntitlements } from '@/services/entitlements/refreshSpaceEntitlements'
import { requestSafenetCheck } from '@/features/safenet-checks/services'

import { getLatestSafeVersion } from '@safe-global/utils/utils/chains'
import type { TxSenderScope } from '@safe-global/views/components/tx-flow/safe-scope/types'
import { concat, dataLength } from 'ethers'
import { encodeNestedTxPayload, supportsNestedTxEnvelope, type NestedTxEnvelope } from '../nestedTxEnvelope'

/**
 * Propose a new transaction
 */
export const dispatchTxProposal = async ({
  chainId,
  safeAddress,
  sender,
  safeTx,
  origin,
  scope,
  nestedTransaction,
}: {
  chainId: string
  safeAddress: string
  sender: string
  safeTx: SafeTransaction
  origin?: string
  scope?: TxSenderScope
  // Verified child tx of a nested approveHash, proposed alongside the parent tx
  nestedTransaction?: NestedTxEnvelope
}): Promise<TransactionDetails> => {
  const safeSDK = getAndValidateSafeSDK(scope)
  const safeTxHash = await safeSDK.getTransactionHash(safeTx)

  let proposedTx: TransactionDetails | undefined
  try {
    proposedTx = await proposeTx(chainId, safeAddress, sender, safeTx, safeTxHash, origin, nestedTransaction)
  } catch (error) {
    txDispatch(TxEvent.PROPOSE_FAILED, { error: asError(error) })
    throw error
  }

  // Dispatch a success event only if the tx is signed
  // Unsigned txs are proposed only temporarily and won't appear in the queue
  if (safeTx.signatures.size > 0) {
    txDispatch(TxEvent.PROPOSED, { txId: proposedTx.txId, nonce: safeTx.data.nonce, chainId, safeAddress })
    void requestSafenetCheck({ chainId, safeAddress, safeTxHash })
  }

  return proposedTx
}

/**
 * Add the sender's signature to an already proposed transaction
 */
export const dispatchTxConfirmation = async ({
  chainId,
  safeAddress,
  sender,
  safeTx,
  txId,
  scope,
}: {
  chainId: string
  safeAddress: string
  sender: string
  safeTx: SafeTransaction
  txId: string
  scope?: TxSenderScope
}): Promise<TransactionDetails> => {
  const safeSDK = getAndValidateSafeSDK(scope)
  const safeTxHash = await safeSDK.getTransactionHash(safeTx)

  let confirmedTx: TransactionDetails | undefined
  try {
    const signature = safeTx.signatures.get(sender.toLowerCase())
    if (!signature) {
      throw new Error(`No signature from ${sender} found on transaction ${txId}`)
    }

    confirmedTx = await confirmTx(chainId, safeTxHash, buildSignatureBytes([signature]))
  } catch (error) {
    txDispatch(TxEvent.SIGNATURE_PROPOSE_FAILED, { txId, chainId, safeAddress, error: asError(error) })
    throw error
  }

  txDispatch(TxEvent.SIGNATURE_PROPOSED, {
    txId: confirmedTx.txId,
    signerAddress: sender,
    nonce: safeTx.data.nonce,
    chainId,
    safeAddress,
  })
  void requestSafenetCheck({ chainId, safeAddress, safeTxHash })

  return confirmedTx
}

/**
 * Sign a transaction
 */
export const dispatchTxSigning = async (
  safeTx: SafeTransaction,
  provider: Eip1193Provider,
  txId?: string,
  scope?: TxSenderScope,
): Promise<SafeTransaction> => {
  const sdk = await getSafeSDKWithSigner(provider, scope)

  let signedTx: SafeTransaction | undefined
  try {
    signedTx = await tryOffChainTxSigning(safeTx, sdk)
  } catch (error) {
    txDispatch(TxEvent.SIGN_FAILED, {
      txId,
      error: asError(error),
    })
    throw error
  }

  txDispatch(TxEvent.SIGNED, { txId })

  return signedTx
}

// We have to manually sign because sdk.signTransaction doesn't support proposers
export const dispatchProposerTxSigning = async (
  safeTx: SafeTransaction,
  wallet: ConnectedWallet,
  scope?: TxSenderScope,
) => {
  const sdk = await getSafeSDKWithSigner(wallet.provider, scope)

  let signature: SafeSignature
  if (isEthSignWallet(wallet)) {
    const txHash = await sdk.getTransactionHash(safeTx)
    signature = await sdk.signHash(txHash)
  } else {
    signature = await sdk.signTypedData(safeTx)
  }

  safeTx.addSignature(signature)

  return safeTx
}

const ZK_SYNC_ON_CHAIN_SIGNATURE_GAS_LIMIT = 4_500_000

// Resolves to the tx hash if the parent executed the approveHash immediately, otherwise to its safeTxHash
const sendApproveHashTx = (
  data: string,
  provider: Eip1193Provider,
  chainId: SafeState['chainId'],
  signerAddress: string,
  safeAddress: string,
): Promise<string> =>
  provider.request({
    method: 'eth_sendTransaction',
    params: [
      {
        from: signerAddress,
        to: safeAddress,
        data,
        gas: chainId === chains.zksync || chainId === chains.lens ? ZK_SYNC_ON_CHAIN_SIGNATURE_GAS_LIMIT : undefined,
      },
    ],
  })

/**
 * On-Chain sign a transaction
 */
export const dispatchOnChainSigning = async (
  safeTx: SafeTransaction,
  txId: string | undefined,
  provider: Eip1193Provider,
  chainId: SafeState['chainId'],
  signerAddress: string,
  safeAddress: string,
  isSafeSigner: boolean,
  executed: boolean,
  safeVersion?: SafeState['version'],
  scope?: TxSenderScope,
): Promise<string> => {
  const sdk = await getSafeSDKWithSigner(provider, scope)
  const safeTxHash = await sdk.getTransactionHash(safeTx)
  // No CGW proposal yet, so derive the id CGW will assign
  const id = txId ?? `multisig_${safeAddress}_${safeTxHash}`
  const eventParams = { txId: id, nonce: safeTx.data.nonce, chainId, safeAddress }

  let txHashOrParentSafeTxHash: string
  try {
    // TODO: This is a workaround until there is a fix for unchecked transactions in the protocol-kit
    let encodedApproveHashTx = await prepareApproveTxHash(safeTxHash, provider, scope)

    // approveHash(bytes32) ABI decoding ignores trailing calldata
    if (isSafeSigner && supportsNestedTxEnvelope(safeVersion)) {
      const payload = encodeNestedTxPayload([{ chainId, safe: safeAddress, ...safeTx.data }])
      encodedApproveHashTx = concat([encodedApproveHashTx, payload])
      console.info('[NestedTxEnvelope] appended child tx envelope to approveHash calldata', {
        childSafeTxHash: safeTxHash,
        payloadBytes: dataLength(payload),
        calldata: encodedApproveHashTx,
      })
    } else if (isSafeSigner) {
      console.info('[NestedTxEnvelope] NOT appending envelope, child Safe version does not qualify', {
        safeVersion: safeVersion ?? 'unknown',
      })
    }

    // Note: SafeWalletProvider returns transaction hash if it exists, otherwise the safeTxHash
    // If the parent immediately executes, this will be the transaction hash of the approveHash
    // otherwise the safeTxHash of it
    txHashOrParentSafeTxHash = await sendApproveHashTx(
      encodedApproveHashTx,
      provider,
      chainId,
      signerAddress,
      safeAddress,
    )

    txDispatch(TxEvent.ONCHAIN_SIGNATURE_REQUESTED, eventParams)
  } catch (err) {
    txDispatch(TxEvent.FAILED, { ...eventParams, error: asError(err) })
    throw err
  }

  txDispatch(TxEvent.ONCHAIN_SIGNATURE_SUCCESS, eventParams)

  // Only a Safe signer creates an approveHash tx we can deep-link to
  if (isSafeSigner) {
    txDispatch(TxEvent.NESTED_SAFE_TX_CREATED, {
      ...eventParams,
      txHashOrParentSafeTxHash,
      parentSafeAddress: signerAddress,
      executed,
      method: 'approveHash',
    })
  }

  // Until the on-chain signature is/has been executed, the safeTx is not
  // signed so we only return its id
  return id
}

// For child Safes too old for an envelope; the child is proposed only once the parent approveHash is accepted
export const dispatchNestedTxCreation = async ({
  safeTx,
  wallet,
  parentSafeAddress,
  parentProvider,
  chainId,
  safeAddress,
  executed,
  origin,
  scope,
  nestedTransaction,
}: {
  safeTx: SafeTransaction
  wallet: ConnectedWallet
  parentSafeAddress: string
  parentProvider: Eip1193Provider
  chainId: SafeState['chainId']
  safeAddress: string
  executed: boolean
  origin?: string
  scope?: TxSenderScope
  nestedTransaction?: NestedTxEnvelope
}): Promise<string> => {
  const sdk = await getSafeSDKWithSigner(parentProvider, scope)
  const safeTxHash = await sdk.getTransactionHash(safeTx)

  const signedTx = await dispatchProposerTxSigning(safeTx, wallet, scope)

  const encodedApproveHashTx = await prepareApproveTxHash(safeTxHash, parentProvider, scope)
  const txHashOrParentSafeTxHash = await sendApproveHashTx(
    encodedApproveHashTx,
    parentProvider,
    chainId,
    parentSafeAddress,
    safeAddress,
  )

  const { txId } = await dispatchTxProposal({
    chainId,
    safeAddress,
    sender: wallet.address,
    safeTx: signedTx,
    origin,
    scope,
    nestedTransaction,
  })

  txDispatch(TxEvent.NESTED_SAFE_TX_CREATED, {
    txId,
    nonce: safeTx.data.nonce,
    chainId,
    safeAddress,
    txHashOrParentSafeTxHash,
    parentSafeAddress,
    executed,
    method: 'approveHash',
  })

  return txId
}

export const dispatchSafeTxSpeedUp = async (
  txOptions: Omit<TransactionOptions, 'nonce'> & { nonce: number },
  txId: string,
  provider: Eip1193Provider,
  chainId: SafeState['chainId'],
  signerAddress: string,
  safeAddress: string,
  nonce: number,
) => {
  const sdk = await getSafeSDKWithSigner(provider)
  const eventParams = { txId, nonce, chainId, safeAddress }
  const signerNonce = txOptions.nonce
  const isSmartAccount = await isSmartContractWallet(chainId, signerAddress)

  // Execute the tx
  let result: TransactionResult | undefined
  try {
    const safeTx = await createExistingTx(chainId, txId)

    // TODO: This is a workaround until there is a fix for unchecked transactions in the protocol-kit
    if (isSmartAccount) {
      const encodedTx = await prepareTxExecution(safeTx, provider)
      const txHash = await provider.request({
        method: 'eth_sendTransaction',
        params: [{ from: signerAddress, to: safeAddress, data: encodedTx }],
      })

      result = {
        hash: txHash,
        transactionResponse: null,
      }
    } else {
      result = await sdk.executeTransaction(safeTx, txOptions)
    }
    txDispatch(TxEvent.EXECUTING, eventParams)
  } catch (error) {
    txDispatch(TxEvent.SPEEDUP_FAILED, { ...eventParams, error: asError(error) })
    throw error
  }

  txDispatch(TxEvent.PROCESSING, {
    ...eventParams,
    txHash: result.hash,
    signerAddress,
    signerNonce,
    gasLimit: txOptions.gasLimit?.toString(),
    txType: 'SafeTx',
  })

  return result.hash
}

export const dispatchCustomTxSpeedUp = async (
  txOptions: Omit<TransactionOptions, 'nonce'> & { nonce: number },
  txId: string,
  to: string,
  data: string,
  provider: Eip1193Provider,
  chainId: string,
  signerAddress: string,
  safeAddress: string,
  nonce: number,
) => {
  const eventParams = { txId, nonce, chainId, safeAddress }
  const signerNonce = txOptions.nonce

  // Execute the tx
  let result: TransactionResponse | undefined
  try {
    const signer = await getUncheckedSigner(provider)
    result = await signer.sendTransaction({ to, data, ...txOptions })
    txDispatch(TxEvent.EXECUTING, eventParams)
  } catch (error) {
    txDispatch(TxEvent.SPEEDUP_FAILED, { ...eventParams, error: asError(error) })
    throw error
  }

  txDispatch(TxEvent.PROCESSING, {
    txHash: result.hash,
    signerAddress,
    signerNonce,
    data,
    to,
    groupKey: result?.hash,
    txType: 'Custom',
    nonce,
    chainId,
    safeAddress,
  })

  return result.hash
}

/**
 * Execute a transaction
 */
export const dispatchTxExecution = async (
  chainId: string,
  safeTx: SafeTransaction,
  txOptions: TransactionOptions,
  txId: string | undefined,
  provider: Eip1193Provider,
  signerAddress: string,
  safeAddress: string,
  isSmartAccount: boolean,
  executed: boolean,
  isSafeSigner = false,
  safeVersion?: SafeState['version'],
  scope?: TxSenderScope,
): Promise<string> => {
  const sdk = await getSafeSDKWithSigner(provider, scope)
  // Never proposed to CGW, so derive the id CGW will assign
  const safeTxHash = await sdk.getTransactionHash(safeTx)
  const id = txId ?? `multisig_${safeAddress}_${safeTxHash}`
  const eventParams = { txId: id, nonce: safeTx.data.nonce, chainId, safeAddress }

  const signerNonce = txOptions.nonce ?? (await getUserNonce(signerAddress))

  // Execute the tx
  let result: TransactionResult | undefined
  try {
    // TODO: This is a workaround until there is a fix for unchecked transactions in the protocol-kit
    if (isSmartAccount) {
      let encodedTx = await prepareTxExecution(safeTx, provider, scope)

      // The parent proposes the child tx as `nestedTransaction`
      if (isSafeSigner && supportsNestedTxEnvelope(safeVersion)) {
        const payload = encodeNestedTxPayload([{ chainId, safe: safeAddress, ...safeTx.data }])
        encodedTx = concat([encodedTx, payload])
        console.info('[NestedTxEnvelope] appended child tx envelope to execTransaction calldata', {
          childSafeTxHash: safeTxHash,
          payloadBytes: dataLength(payload),
          calldata: encodedTx,
        })
      }

      const txHash = await provider.request({
        method: 'eth_sendTransaction',
        params: [{ from: signerAddress, to: safeAddress, data: encodedTx }],
      })

      result = {
        hash: txHash,
        transactionResponse: null,
      }
    } else {
      result = await sdk.executeTransaction(safeTx, txOptions)
    }
  } catch (error) {
    txDispatch(TxEvent.FAILED, { ...eventParams, error: asError(error) })
    throw error
  }

  // A queuing executor returns its Safe's safeTxHash, not an on-chain tx hash
  if (isSmartAccount && !executed) {
    txDispatch(TxEvent.NESTED_SAFE_TX_CREATED, {
      ...eventParams,
      txHashOrParentSafeTxHash: result.hash,
      parentSafeAddress: signerAddress,
      executed: false,
      method: 'execTransaction',
    })

    return id
  }

  txDispatch(TxEvent.EXECUTING, { ...eventParams })

  txDispatch(TxEvent.PROCESSING, {
    ...eventParams,
    nonce: safeTx.data.nonce,
    txHash: result.hash,
    signerAddress,
    signerNonce,
    gasLimit: txOptions.gasLimit?.toString(),
    txType: 'SafeTx',
  })

  return id
}

export const dispatchBatchExecution = async (
  txs: TransactionDetails[],
  multiSendContract: MultiSendCallOnlyContractImplementationType,
  multiSendTxData: `0x${string}`,
  provider: Eip1193Provider,
  chainId: string,
  signerAddress: string,
  safeAddress: string,
  overrides: Omit<Overrides, 'nonce'> & { nonce: number },
  nonce: number,
) => {
  const groupKey = multiSendTxData

  let result: TransactionResponse
  const txIds = txs.map((tx) => tx.txId)
  let signerNonce = overrides.nonce
  let txData = multiSendContract.encode('multiSend', [multiSendTxData])

  try {
    if (signerNonce === undefined || signerNonce === null) {
      signerNonce = await getUserNonce(signerAddress)
    }
    const signer = await getUncheckedSigner(provider)

    result = await signer.sendTransaction({
      to: multiSendContract.getAddress(),
      value: '0',
      data: txData,
      ...overrides,
    })

    txIds.forEach((txId) => {
      txDispatch(TxEvent.EXECUTING, { txId, groupKey, nonce, chainId, safeAddress })
    })
  } catch (err) {
    txIds.forEach((txId) => {
      txDispatch(TxEvent.FAILED, { txId, error: asError(err), groupKey, nonce, chainId, safeAddress })
    })
    throw err
  }
  const txTo = multiSendContract.getAddress()

  txIds.forEach((txId) => {
    txDispatch(TxEvent.PROCESSING, {
      txId,
      txHash: result.hash,
      groupKey,
      signerNonce,
      signerAddress,
      txType: 'Custom',
      data: txData,
      to: txTo,
      nonce,
      chainId,
      safeAddress,
    })
  })

  return result!.hash
}

/**
 * Execute a module transaction
 */
export const dispatchModuleTxExecution = async (
  tx: Transaction,
  provider: Eip1193Provider,
  chainId: string,
  safeAddress: string,
): Promise<string> => {
  const id = JSON.stringify(tx)

  let result: TransactionResponse | undefined
  try {
    const browserProvider = createWeb3(provider)
    const signer = await browserProvider.getSigner()

    txDispatch(TxEvent.EXECUTING, { groupKey: id, chainId, safeAddress })
    result = await signer.sendTransaction(tx)
  } catch (error) {
    txDispatch(TxEvent.FAILED, { groupKey: id, chainId, safeAddress, error: asError(error) })
    throw error
  }

  txDispatch(TxEvent.PROCESSING_MODULE, {
    groupKey: id,
    txHash: result.hash,
  })

  result
    ?.wait()
    .then((receipt) => {
      if (receipt === null) {
        txDispatch(TxEvent.FAILED, {
          groupKey: id,
          chainId,
          safeAddress,
          error: new Error('No transaction receipt found'),
        })
      } else if (didRevert(receipt)) {
        txDispatch(TxEvent.REVERTED, {
          groupKey: id,
          chainId,
          safeAddress,
          error: new Error('Transaction reverted by EVM'),
        })
      } else {
        txDispatch(TxEvent.PROCESSED, { groupKey: id, chainId, safeAddress, txHash: result?.hash })
      }
    })
    .catch((error) => {
      txDispatch(TxEvent.FAILED, { groupKey: id, chainId, safeAddress, error: asError(error) })
    })

  return result?.hash
}

export async function dispatchSafeAppsTx(
  args: { safeAppRequestId: RequestId; txId?: string } & (
    { safeTx: SafeTransaction; provider: Eip1193Provider } | { safeTxHash: string }
  ),
): Promise<string> {
  let safeTxHash: string
  if ('safeTx' in args && 'provider' in args) {
    const { safeTx, provider } = args
    const sdk = await getSafeSDKWithSigner(provider)
    safeTxHash = await sdk.getTransactionHash(safeTx)
  } else {
    safeTxHash = args.safeTxHash
  }

  const { txId, safeAppRequestId } = args
  txDispatch(TxEvent.SAFE_APPS_REQUEST, { safeAppRequestId, safeTxHash, txId })
  return safeTxHash
}

// Typed CGW refusals (422 simulation, 402 quota, 409 option, 429 limit, chain-route 403) let the UI block, retry or fall back.
const getRelayError = (error: unknown, sponsorSpaceId?: string | null): Error =>
  getRelaySimulationError(error) ??
  (sponsorSpaceId ? getQuotaExceededError(error) : undefined) ??
  getGasPaymentOptionUnavailableError(error) ??
  getRelayLimitReachedError(error) ??
  (sponsorSpaceId ? undefined : getRelayerUnavailableError(error)) ??
  asError(error)

const isStaleEntitlementsError = (error: Error): boolean =>
  error instanceof QuotaExceededError || error instanceof GasPaymentOptionUnavailableError

export const dispatchTxRelay = async (
  safeTx: SafeTransaction,
  safe: SafeState,
  txId: string,
  chain: Chain,
  gasLimit?: string | number | bigint,
  acceptUnverifiedSimulation?: boolean,
  scope?: TxSenderScope,
  /** A Safe Pro Workspace paying for the relay out of its allowance; without it the chain's relayer policy applies. */
  sponsorSpaceId?: string | null,
) => {
  const store = getStoreInstance()
  const readOnlySafeContract = await getReadOnlyCurrentGnosisSafeContract(safe, scope)
  const safeSDK = getAndValidateSafeSDK(scope)
  const safeTxHash = await safeSDK.getTransactionHash(safeTx)

  let transactionToRelay = safeTx
  const data = readOnlySafeContract.encode('execTransaction', [
    transactionToRelay.data.to,
    transactionToRelay.data.value,
    transactionToRelay.data.data,
    transactionToRelay.data.operation,
    transactionToRelay.data.safeTxGas,
    transactionToRelay.data.baseGas,
    transactionToRelay.data.gasPrice,
    transactionToRelay.data.gasToken,
    transactionToRelay.data.refundReceiver,
    transactionToRelay.encodedSignatures(),
  ])

  try {
    const version = safe.version ?? getLatestSafeVersion(chain)
    const relayResponse = sponsorSpaceId
      ? await store
          .dispatch(
            relayApi.endpoints.spaceRelayRelayV1.initiate({
              spaceId: sponsorSpaceId,
              chainId: safe.chainId,
              spaceRelayDto: { to: safe.address.value, data, version, safeTxHash, acceptUnverifiedSimulation },
            }),
          )
          .unwrap()
          .then((response) => {
            // The Workspace just spent a sponsored transaction; every meter on screen should say so.
            refreshSpaceEntitlements(store.dispatch, sponsorSpaceId)
            return response
          })
      : await store
          .dispatch(
            relayApi.endpoints.relayRelayV1.initiate({
              chainId: safe.chainId,
              relayDto: {
                to: safe.address.value,
                data,
                gasLimit: gasLimit?.toString(),
                version,
                safeTxHash,
                acceptUnverifiedSimulation,
              },
            }),
          )
          .unwrap()
    const taskId = relayResponse.taskId

    if (!taskId) {
      throw new Error('Transaction could not be relayed')
    }

    txDispatch(TxEvent.RELAYING, {
      taskId,
      txId,
      nonce: safeTx.data.nonce,
      chainId: safe.chainId,
      safeAddress: safe.address.value,
    })

    // Monitor relay tx
    waitForRelayedTx(taskId, [txId], safe.chainId, safe.address.value, safeTx.data.nonce)
  } catch (error) {
    const finalError = getRelayError(error, sponsorSpaceId)
    // A space-route refusal means the meter on screen is stale.
    if (sponsorSpaceId && isStaleEntitlementsError(finalError)) refreshSpaceEntitlements(store.dispatch, sponsorSpaceId)
    txDispatch(TxEvent.FAILED, {
      txId,
      error: finalError,
      nonce: safeTx.data.nonce,
      chainId: safe.chainId,
      safeAddress: safe.address.value,
    })
    throw finalError
  }
}

export const dispatchBatchExecutionRelay = async (
  txs: TransactionDetails[],
  multiSendContract: MultiSendCallOnlyContractImplementationType,
  multiSendTxData: `0x${string}`,
  chainId: string,
  safeAddress: string,
  safeVersion: string,
  sponsorSpaceId?: string | null,
) => {
  const store = getStoreInstance()
  const to = multiSendContract.getAddress()

  const data = multiSendContract.encode('multiSend', [multiSendTxData])
  const groupKey = multiSendTxData

  let relayResponse
  try {
    relayResponse = sponsorSpaceId
      ? await store
          .dispatch(
            relayApi.endpoints.spaceRelayRelayV1.initiate({
              spaceId: sponsorSpaceId,
              chainId,
              spaceRelayDto: { to, data, version: safeVersion },
            }),
          )
          .unwrap()
          .then((response) => {
            refreshSpaceEntitlements(store.dispatch, sponsorSpaceId)
            return response
          })
      : await store
          .dispatch(relayApi.endpoints.relayRelayV1.initiate({ chainId, relayDto: { to, data, version: safeVersion } }))
          .unwrap()
  } catch (error) {
    const finalError = getRelayError(error, sponsorSpaceId)
    if (sponsorSpaceId && isStaleEntitlementsError(finalError)) refreshSpaceEntitlements(store.dispatch, sponsorSpaceId)
    txs.forEach(({ txId }) => {
      txDispatch(TxEvent.FAILED, {
        txId,
        chainId,
        safeAddress,
        error: finalError,
        groupKey,
      })
    })
    throw finalError
  }

  const taskId = relayResponse.taskId
  txs.forEach(({ txId, detailedExecutionInfo }) => {
    if (isMultisigExecutionInfo(detailedExecutionInfo)) {
      txDispatch(TxEvent.RELAYING, { taskId, txId, groupKey, nonce: detailedExecutionInfo.nonce, chainId, safeAddress })
    }
  })

  // Monitor relay tx
  waitForRelayedTx(
    taskId,
    txs.map((tx) => tx.txId),
    chainId,
    safeAddress,
    isMultisigExecutionInfo(txs[0].detailedExecutionInfo) ? txs[0].detailedExecutionInfo.nonce : 0,
    groupKey,
  )
}
